import { Button } from '@cian/ui-kit/button';
import { Outside } from '@cian/ui-kit/services';
import * as React from 'react';

import * as styles from './Price.css';
import { PriceCorrectionHint } from './PriceCorrectionHint';
import {
  getIsSquareMeterPrice,
  getPriceLabel,
  getPriceTitle,
  getPriceType,
  getPriceTypeLabel,
  PRICE_TYPES,
} from './helpers';
import { EPriceType, FDealType, FOfferType } from '../../../../../JsonQuery';
import { IJsonQueryRangeValue } from '../../../../../api-models/common/json_query';
import { useDebouncedCallback } from '../../../utils/useDebouncedCallback';
import { RangeInput } from '../../RangeInput';
import { Option, Select } from '../../Select';

interface IPriceProps {
  dealType: FDealType;
  offerType: FOfferType;
  price: IJsonQueryRangeValue | null;
  defaultPriceLabel?: string;
  isSquareMeterPrice: boolean | null;
  showCorrectionHint?: boolean;
  onPriceMinChange(gte: number | null): void;
  onPriceMaxChange(lte: number | null): void;
  onPriceTypeChange(isSquareMeterPrice: boolean | null): void;
}

export const Price: React.FC<IPriceProps> = ({
  dealType,
  offerType,
  price,
  defaultPriceLabel = 'Цена',
  isSquareMeterPrice,
  showCorrectionHint = false,
  onPriceMinChange,
  onPriceMaxChange,
  onPriceTypeChange,
}) => {
  const { gte: priceMin = null, lte: priceMax = null } = price || {};
  const isPriceTypeAvailable = dealType === FDealType.Sale && (offerType & FOfferType.Urban) !== 0;

  const [isOpen, setIsOpen] = React.useState(false);

  const priceTitle = getPriceTitle(priceMin, priceMax) || undefined;
  const priceLabel = getPriceLabel(priceMin, priceMax);
  const priceType = getPriceType(isSquareMeterPrice);
  const priceTypeLabel = getPriceTypeLabel(priceType);

  const handleClick = React.useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleClose = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const handlePriceMinChange = useDebouncedCallback((gte: number | null) => {
    onPriceMinChange(gte);
  }, 800);

  const handlePriceMaxChange = useDebouncedCallback((lte: number | null) => {
    onPriceMaxChange(lte);
  }, 800);

  return (
    <Outside onOutside={handleClose} active={isOpen}>
      <div className={styles['container']} data-name="FilterPrice">
        <Button theme="stroke_secondary" size="XS" title={priceTitle} onClick={handleClick}>
          {priceLabel || defaultPriceLabel}
        </Button>
        {isOpen && (
          <div className={styles['dropdown']}>
            <div className={styles['dropdown-filter']}>
              <RangeInput
                min={0}
                max={999999999999}
                joined={false}
                postfix={'\u20bd'}
                valueMin={priceMin}
                valueMax={priceMax}
                onChangeMin={handlePriceMinChange}
                onChangeMax={handlePriceMaxChange}
                isForceUpdate
                allowMaxBelowMin
              />
            </div>
            {isPriceTypeAvailable && (
              <div className={styles['dropdown-filter']}>
                <Select
                  withoutArrow
                  buttonAppearance={'underlined'}
                  value={priceType || EPriceType.Total}
                  label={priceTypeLabel}
                  onChange={(_, value: EPriceType) => onPriceTypeChange(getIsSquareMeterPrice(value))}
                >
                  {PRICE_TYPES.map(priceType => (
                    <Option key={`${priceType.label}_${priceType.value}`} value={priceType.value}>
                      {priceType.label}
                    </Option>
                  ))}
                </Select>
              </div>
            )}
            {showCorrectionHint && <PriceCorrectionHint />}
          </div>
        )}
      </div>
    </Outside>
  );
};
